import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Dropdown, HooksService, Radio, RadioGroup } from 'basic-components';
import { ArrowLeft, ArrowRight, Pending, Running, Success } from 'basic-components/icons';
import { ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DateFormatPicker } from 'primary-components';

import { ncsvCreatorActions } from 'actions/modules/tools';

import { getHeaderChanges, getNameWithoutType, getRowsChanges, onFail } from '../CSVService';
import MessageContainer from '../MessageContainer';

import dayjs from 'dayjs';

import { When } from 'react-if';

import classNames from 'classnames';

const transformSize = 200
const VALUE_TYPES = [{
  value: "DB",
  label: "Double",
  tooltip: "The values in column are numbers with decimal places. In example 3.14."
}, {
  value: "S",
  label: "String",
  tooltip: "The values in column are texts. In example New York."
}, {
  value: "I",
  label: "Integer",
  tooltip: "The values in column are numbers without decimal places. In example 3."
},{
  value: "D",
  label: "Date",
  tooltip: "The values in column are dates. In example 2022-01-01"
}]

const isColumnSetUp = (config = {}) => config?.type && (config?.type !== "D" || (config?.dateFormat && config?.timezone))

const getValueOrMDString = (config = {}) => {
  const { type, columnType = "Value" } = config
  if(!type || !columnType) {
    return ""
  }
  let toReturn = ""
  if(columnType === "Metadata") {
    toReturn += "MD-"
  }
  return toReturn + type;
}

const getStatusIcon = (activeColumn, columnName, columnConfigs) => {
  const config = columnConfigs[columnName]
  if(isColumnSetUp(config)) {
    return (
      <Success
          bgColor="green-fresh"
          color="white-base"
          height={16}
          width={16}
      />
    )
  } else if(activeColumn === columnName) {
    return (
      <Running
          bgColor="blue-bright"
          color="white-base"
          height={16}
          width={16}
      />
    )
  } 
  return (
    <Pending
        bgColor="grey-base"
        color="white-base"
        height={16}
        width={16}
    />
  )
}

const DATE_COLUMN = "Date"
const EMPTY_ARRAY = [];
const ColumnsStep = ({ handler, setIsError, setIsSucess, setErrorCells, worksheetRef, ref }) => {
  const dispatch = useDispatch(),
        getColumns = useCallback(() => worksheetRef.current?.getColumns(), [worksheetRef]),
        getRows = useCallback(() => worksheetRef.current?.getRows(), [worksheetRef]),
        [columnNames, setColumnNames] = useState(() => getColumns().map(c => c?.columnName)),
        columnsRightToDate = useMemo(() => columnNames.slice(columnNames.indexOf(DATE_COLUMN) + 1).map(c => getNameWithoutType(c)), [columnNames]),
        columnConfigs = useSelector(state => state.toolsState.ncsvCreatorState.columnConfigs),
        [activeColumn, setActiveColumn] = useState(columnsRightToDate[0]),
        prevActiveColumn = HooksService.usePrevious(activeColumn),
        [columnsAreOverflowed, setColumnsAreOverflowed] = useState(false),
        [columnsTransform, setColumnsTransform] = useState(0),
        [errorMessage, setErrorMessage] = useState(undefined),
        [successMessage, setSuccessMessage] = useState(undefined),
        setColumnConfigs = useCallback(value => dispatch(ncsvCreatorActions.changeValue(value, 'columnConfigs')), [dispatch]),
        onChange = useCallback((value, { name }) => setColumnConfigs(ReduxService.updateObject(columnConfigs, {
          [activeColumn]: ReduxService.updateObject(columnConfigs[activeColumn], { [name]: value })
        })), [activeColumn, columnConfigs, setColumnConfigs]),
        columnsRef = useRef(),
        windowWidth = HooksService.useWindowSize(),
        [isValid, setIsValid] = useState(),
        configuredColumnsLength = useMemo(() => columnsRightToDate.reduce((prev, curr) => {
          const config = columnConfigs[curr] || {}
          if(isColumnSetUp(config)) {
            return prev + 1;
          }
          return prev;
        }, 0), [columnConfigs, columnsRightToDate]),
        currentConfig = useMemo(() => columnConfigs[activeColumn] || {}, [columnConfigs, activeColumn]),
        prevCurrentConfig = HooksService.usePrevious(currentConfig),
        defaultTimezone = useMemo(() => TimeService.TIME_ZONES.find(t => t?.value === currentConfig?.timezone), [currentConfig]),
        defaultType = useMemo(() => VALUE_TYPES.find(t => t?.value === currentConfig?.type), [currentConfig]),
        defaultDateFormat = useMemo(() => TimeService.basicDateFormats.find(d => d?.value === currentConfig?.dateFormat), [currentConfig]),
        typeDropdownRef = useRef(),
        timezoneDropdownRef = useRef(),
        dateFormatDropdownRef = useRef(),
        handleColumnFormat = useCallback(() => formatColumn({ getColumns, activeColumn, currentConfig, getRows, setIsSucess, setIsError, setErrorCells, handler, worksheetRef, setErrorMessage, setSuccessMessage, errorMessage, successMessage }), [activeColumn, currentConfig, errorMessage, getColumns, getRows, handler, setErrorCells, setIsError, setIsSucess, successMessage, worksheetRef]),
        validateForForm = useCallback(() => validate({ columnConfigs, columnsRightToDate, getRows, getColumns, setErrorCells, setIsValid, setIsSucess, setIsError, setSuccessMessage, setErrorMessage }), [columnConfigs, columnsRightToDate, getColumns, getRows, setErrorCells, setIsError, setIsSucess])

  useEffect(() => {
    let width = 0
    const childs = columnsRef.current?.childNodes || []
    for(const child of childs) {
      width += child.clientWidth
    }
    setColumnsAreOverflowed(childs.length > 0 && width > columnsRef.current.clientWidth)
  }, [columnsRightToDate, windowWidth])

  useEffect(() => {
    if(currentConfig.type && (currentConfig?.type !== "D" || (currentConfig?.dateFormat && currentConfig?.timezone)) && JSON.stringify(currentConfig) !== JSON.stringify(prevCurrentConfig)) {
      handleColumnFormat()
    }
  }, [currentConfig, handleColumnFormat, prevCurrentConfig])

  useEffect(() => {
    if(activeColumn !== prevActiveColumn) {
      typeDropdownRef.current.dropdown.current.setValue(defaultType)
    }
  }, [activeColumn, defaultType, prevActiveColumn])

  useEffect(() => {
    if(activeColumn !== prevActiveColumn) {
      timezoneDropdownRef.current.dropdown.current.setValue(defaultTimezone)
    }
  }, [activeColumn, defaultTimezone, prevActiveColumn])

  useEffect(() => {
    if(activeColumn !== prevActiveColumn) {
      dateFormatDropdownRef.current.dropdown.current.setValue(defaultDateFormat)
    }
  }, [activeColumn, defaultDateFormat, prevActiveColumn])

  useImperativeHandle(ref, () => ({ 
    validateForForm,
    isValid: () => isValid,
    setColumnNames
  }))

  return (
    <div className="ncsv-creator__top__step ncsv-creator__top__step--columns">
      <div 
          className={classNames(
            "ncsv-creator__top__step__carousel",
            { "ncsv-creator__top__step__carousel--is-overflowed": columnsAreOverflowed }
          )}
          ref={columnsRef}
      >
        <When condition={columnsAreOverflowed}>
          {() => (
            <div 
                className="ncsv-creator__top__step__carousel__left"
                onClick={() => {
                  const transformNew = columnsTransform + transformSize
                  setColumnsTransform(transformNew > 0 ? 0 : transformNew)
                }}
            >
              <ArrowLeft
                  height={24}
                  width={24}
              />
            </div>
          )}
        </When>
        <div 
            className="ncsv-creator__top__step__carousel__content"
            style={{ transform: "translateX(" + columnsTransform + "px)" }}
        >
          {columnsRightToDate.map((columnName, index) => (
            <div 
                className={classNames(
                  "ncsv-creator__top__step__carousel__content__item",
                  { "ncsv-creator__top__step__carousel__content__item--is-active": activeColumn === columnName }
                )}
                key={`Column${columnName}${index}`}
                onClick={() => setActiveColumn(columnName)}
            >
              <div className="ncsv-creator__top__step__carousel__content__item__status">
                {getStatusIcon(activeColumn, columnName, columnConfigs)}
              </div>
              <div className="ncsv-creator__top__step__carousel__content__item__grey">
                {getValueOrMDString(columnConfigs[columnName])}
              </div>
              <div className="ncsv-creator__top__step__carousel__content__item__text">
                {getNameWithoutType(columnName)}
              </div>
            </div>
          ))}
        </div>
        <When condition={columnsAreOverflowed}>
          {() => (
            <div 
                className="ncsv-creator__top__step__carousel__right"
                onClick={() => {
                  const transformNew = columnsTransform - transformSize
                  setColumnsTransform(Math.abs(transformNew) + columnsRef.current.clientWidth < columnsRef.current.scrollWidth ? transformNew : -1 * (columnsRef.current.scrollWidth - columnsRef.current.clientWidth))
                }}
            >
              <ArrowRight
                  height={24}
                  width={24}
              />
            </div>
          )}
        </When>
      </div>
      <div className="ncsv-creator__top__step__counter">
        <div className="ncsv-creator__top__step__counter__text">
          Configured columns {configuredColumnsLength}/{columnsRightToDate.length}
        </div>
      </div>
      <div className="ncsv-creator__top__step__column-box">
        <div className="ncsv-creator__top__step__column-box__label">
          {getNameWithoutType(activeColumn)}    
        </div>
        <div className="ncsv-creator__top__step__column-box__column-type">
          <RadioGroup
              name="columnType"
              onChange={onChange}
              selectedValue={currentConfig?.columnType || "Value"}
          >
            <Radio name="Value">
              Value
            </Radio>
            <Radio name="Metadata">
              Metadata
            </Radio>
          </RadioGroup>
        </div>
        <div className={classNames(
          "ncsv-creator__top__step__column-box__type",
          { "ncsv-creator__top__step__column-box__type--is-date-visible": currentConfig?.type === "D" }
        )}>
          <div className="ncsv-creator__top__step__column-box__type__top">
            <Dropdown
                defaultValue={defaultType}
                fixed
                name="type"
                noError
                onChange={onChange}
                options={VALUE_TYPES}
                placeholder="Type"
                ref={typeDropdownRef}
            />
          </div>
          <div className={classNames(
            "ncsv-creator__top__step__column-box__type__bottom",
            { "ncsv-creator__top__step__column-box__type__bottom--is-visible": currentConfig?.type === "D" }
          )}>
            <DateFormatPicker
                deteFormat={currentConfig?.dateFormat}
                fixed
                name="dateFormat"
                noError
                onChange={onChange}
                placeholder="Date format"
                ref={dateFormatDropdownRef}
                size="small"
            />
            <Dropdown
                defaultValue={defaultTimezone}
                fixed
                name="timezone"
                noError
                onChange={onChange}
                options={TimeService.TIME_ZONES}
                placeholder="Select timezone"
                ref={timezoneDropdownRef}
                size="small"
            />
          </div>
        </div>
      </div>
      <MessageContainer
          errorMessage={errorMessage}
          successMessage={successMessage}
      />
    </div>
  )
}

ColumnsStep.displayName = "ColumnsStep"

ColumnsStep.propTypes = {
  handler: PropTypes.func.isRequired,
  setErrorCells: PropTypes.func.isRequired,
  setIsError: PropTypes.func.isRequired,
  setIsSucess: PropTypes.func.isRequired,
  worksheetRef: PropTypes.object.isRequired,
}

export default ColumnsStep

const onSucces = ({ worksheetRef, headerChanges, rowChanges, setIsSucess, setIsError, activeColumn, setErrorMessage, setSuccessMessage }) => {
  worksheetRef.current?.handleChanges([
    ...headerChanges,
    ...rowChanges,
  ])
  setErrorMessage()
  setSuccessMessage(`Column ${activeColumn} was formatted succesfully.`)
  setIsSucess(true)
  setIsError(false)
}

const formatColumn = ({ getColumns, activeColumn, currentConfig, getRows, setIsSucess, setIsError, setErrorCells, handler, worksheetRef, setErrorMessage, setSuccessMessage }) => {
  const columns = getColumns(),
        colIndex = columns.findIndex(c => getNameWithoutType(c?.columnName) === activeColumn)
  if(colIndex === -1) {
    setSuccessMessage()
    setErrorMessage(`Could not find column ${activeColumn}. Please try again.`)
    return
  }
  const successRows = [], failCells = [],
        newColumnType = getValueOrMDString(currentConfig),
        columnId = columns[colIndex].columnId,
        rows = getRows(),
        newValue = `${activeColumn}(${newColumnType})`
  if(currentConfig.type === "D") {
    const { dateFormat, timezone } = currentConfig
    let initDateElementIndex = EMPTY_ARRAY.findIndex(initDataColumn => initDataColumn.columnId === columnId)
    if(initDateElementIndex === -1){
      EMPTY_ARRAY.push({
        columnId,
        rows: rows.filter(row => !String(row.rowId).startsWith("header")).map(row => row.cells[colIndex]?.text)
      })
      initDateElementIndex = EMPTY_ARRAY.length - 1
    }
    const rowChanges = getRowsChanges({ rows, colIndex, dateFormat, timezone, successRows, failCells, columnId, EMPTY_ARRAY: EMPTY_ARRAY[initDateElementIndex]?.rows })
    const headerChanges = getHeaderChanges({ rows, colIndex, columnId, newValue })
    if(failCells?.length === 0 ) {
      onSucces({ worksheetRef, headerChanges, rowChanges, setIsSucess, setIsError, activeColumn, setErrorMessage, setSuccessMessage })
    } else {
      onFail({ successRows, setIsSucess, setIsError, failCells, setSuccessMessage, setErrorMessage })
    }
    setErrorCells(failCells)
  } else {
    setErrorMessage()
    setSuccessMessage()
    handler({ handleChanges: worksheetRef.current?.handleChanges, columnType: newColumnType, rows, columns, columnId })
  }
}

const validate = ({ columnConfigs, columnsRightToDate, getRows, getColumns, setErrorCells, setIsValid, setIsSucess, setIsError, setSuccessMessage, setErrorMessage }) => {
  const isValid = getIsValid({ columnConfigs, columnsRightToDate })
  if (!isValid) {
    setSuccessMessage()
    setErrorMessage("All columns has to be set up.")
    return isValid;
  }
  const failCells = []
  validateDateFormat(columnConfigs, getColumns, getRows, failCells)
  if (failCells.length > 0) {
    onValidationFail({ failCells, setErrorCells, setIsValid, setIsSucess, setIsError, setSuccessMessage, setErrorMessage })
    return false;
  } else {
    onValidationSuccess({ setErrorCells, setIsSucess, isValid, setIsValid, setIsError, setErrorMessage })
    return true;
  }
}

const getIsValid = ({ columnConfigs, columnsRightToDate }) => {
  if (Object.values(columnConfigs).filter(v => v !== undefined).length < columnsRightToDate.length) {
    return false
  }
  const isAnyInvalid = Object.entries(columnConfigs).some(([key, config]) => {
    if (columnsRightToDate.includes(key)) {
      if (!config?.type || (config?.type === "D" && (!config?.dateFormat || !config?.timezone))) {
        return true
      }
    }
  })
  return !isAnyInvalid
}

const onValidationFail = ({ failCells, setErrorCells, setIsValid, setIsSucess, setIsError, setSuccessMessage, setErrorMessage }) => {
  setSuccessMessage()
  setErrorMessage(`Some rows does not match proper date format. Please verify the data. Failed rows: ${failCells.map(cell => cell?.rowNumber + 1)}`)
  setErrorCells(failCells)
  setIsValid(false)
  setIsSucess(false)
  setIsError(true)
}

const onValidationSuccess = ({ setErrorCells, setIsSucess, isValid, setIsValid, setIsError, setErrorMessage }) => {
  setErrorMessage()
  setErrorCells([])
  setIsSucess(isValid)
  setIsValid(isValid)
  setIsError(!isValid)
}

const validateDateFormat = (columnConfigs, getColumns, getRows, failCells) => {
  Object.entries(columnConfigs).filter(([, config]) => config?.type === "D" && config?.dateFormat && config?.timezone).forEach(([columnName]) => {
    const columns = getColumns(), columnIndex = columns.findIndex(c => getNameWithoutType(c.columnId) === columnName), rows = getRows()
    rows.filter(row => !String(row.rowId).startsWith("header")).forEach(row => {
      try {
        dayjs.tz(row.cells[columnIndex]?.text, "YYYY-MM-DDTHH:mm:ss", "UTC")
      } catch {
        failCells.push({ columnNumber: columnIndex, rowNumber: row?.rowId, message: "There was a problem while formatting your date. Please change the date format." })
      }
    })
  })
}